import { useEffect, useState } from 'react';

import { useGetEligibleMenuUpsellItems } from 'bento-ordering/cart/utils/menuUpsellSelectors';

import { shuffle } from 'bento-ordering/utils/arrayUtils';
import { CUSTOM_ITEMS } from 'bento-ordering/cart/constants/upsellTypes';

/**
 * The purpose of this hook is to prevent excessive rerenders of the MenuUpsell
 * container - specifically for custom items - by calculating a diff of the items currently
 * rendered and the updated ones in the store.
 */
const useGetUpdatedUpsellItems = () => {
  const [currentUpsellItemsList, setCurrentUpsellItemsList] = useState([]);

  const { items, upsellType } = useGetEligibleMenuUpsellItems();

  // Doing this to account for deep equalities by comparing string diff.
  const stringifiedItems = JSON.stringify(items);

  const getNewUpsellItemListToRender = () => {
    // First check which upsell items currently being rendered should remain
    const incomingItemIds = items.map(i => i.id);
    const currentUpsellItemsToKeep = currentUpsellItemsList.filter(
      currentItem => incomingItemIds.includes(currentItem.id)
    );

    // Next, we determine how many upsell items we need to update
    const currentUpsellItemsToKeepIds = currentUpsellItemsToKeep.map(i => i.id);
    const numItemsToSlice = 3 - currentUpsellItemsToKeep.length;

    // Finally, we filter out the items that remain in the current list from the ones
    // eligible to be added, shuffle this filtered list, and slice the list by the amount
    // calculated in the previous step
    const itemsEligibleForUpsellItemsList = items.filter(
      item => !currentUpsellItemsToKeepIds.includes(item.id)
    );
    const itemsToAddToUpsellItemsList = shuffle(
      itemsEligibleForUpsellItemsList
    ).slice(0, numItemsToSlice);

    return currentUpsellItemsToKeep.concat(itemsToAddToUpsellItemsList);
  };

  useEffect(() => {
    let upsellList;
    if (upsellType === CUSTOM_ITEMS) {
      upsellList = getNewUpsellItemListToRender();
    } else {
      upsellList = items.slice(0, 3);
    }

    setCurrentUpsellItemsList(upsellList);
  }, [stringifiedItems]);

  return currentUpsellItemsList;
};

export default useGetUpdatedUpsellItems;
